// src/utils/sanitizeHtml.ts
//
// The single source of truth for turning stored lesson HTML into something
// safe to put in the DOM.
//
// Lesson decks travel between people as JSON files. Every HTML string in one
// of those files — block content, captions, card bodies, generated citations,
// draw-over SVG — is untrusted input authored by whoever made the deck. It is
// rendered with dangerouslySetInnerHTML, so without a sanitizer a shared deck
// is a stored-XSS delivery vehicle: script in the victim's origin, reading
// every other lesson in their localStorage.
//
// Two profiles, because one allowlist cannot honestly serve both kinds of
// content:
//
//   sanitizeHtml()              Rich text and SVG *fragments* — what the TipTap
//                               editor and the fabric shape builder produce.
//                               Renders directly into the app's own DOM, so the
//                               allowlist is tight.
//
//   sanitizeEmbeddedDocument()  Whole HTML *documents* the user uploads as a
//                               layout, and code-block previews. These are a
//                               deliberate feature and legitimately need
//                               <style> and page-structure tags. They render
//                               inside a sandboxed iframe, never in our DOM.
//
// Both strip the same script vectors. Neither trusts the input.

import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';

/**
 * Tags real lesson content uses. Derived from the TipTap extension set in
 * RichTextEditor.js (StarterKit + Underline/TextStyle/Color/FontFamily/
 * Highlight/Sub/Superscript/TextAlign/Link/Table*) and from the HTML actually
 * present in the shipped templates and fixtures — not guessed narrow.
 */
const RICH_TEXT_TAGS = [
  'p', 'br', 'hr', 'div', 'span',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins',
  'mark', 'small', 'sub', 'sup',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
  'a', 'img', 'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'caption', 'colgroup', 'col'
];

/**
 * Shape-only SVG subset.
 *
 * image_drawover blocks store the output of fabric's canvas.toSVG()
 * (InteractiveContentTools.js) as raw markup and render it through
 * dangerouslySetInnerHTML. Blocking <svg> outright would silently blank every
 * annotated image in every deck, so the drawing primitives are allowed and the
 * script vectors inside SVG are forbidden instead (see FORBIDDEN_TAGS:
 * foreignObject, animate, set, script).
 */
const SVG_SHAPE_TAGS = [
  'svg', 'g', 'defs', 'desc', 'title',
  'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
  'text', 'tspan', 'image',
  'linearGradient', 'radialGradient', 'stop',
  'clipPath', 'mask', 'pattern', 'use'
];

const RICH_TEXT_ATTR = [
  // Presentation. `style` carries TipTap's colour, font-family and text-align
  // marks and is heavily used by the shipped templates; DOMPurify parses and
  // re-serializes it through the CSS allowlist, so expression()/url(javascript:)
  // do not survive.
  'class', 'style', 'id', 'title', 'dir', 'lang',
  // Links
  'href', 'target', 'rel',
  // Images
  'src', 'alt', 'width', 'height', 'loading', 'decoding',
  // Tables — colwidth is TipTap's resizable-column attribute
  'colspan', 'rowspan', 'headers', 'scope', 'abbr', 'colwidth',
  // TipTap round-trip attributes. ALLOW_DATA_ATTR is off, so these are
  // enumerated rather than blanket-allowed.
  'data-color', 'data-text-align', 'data-type', 'data-id',
  'data-background-color', 'data-colwidth'
];

const SVG_SHAPE_ATTR = [
  'viewbox', 'xmlns', 'xmlns:xlink', 'version', 'preserveaspectratio',
  'transform', 'd', 'points',
  'fill', 'fill-rule', 'fill-opacity',
  'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
  'stroke-dasharray', 'stroke-dashoffset', 'stroke-miterlimit', 'stroke-opacity',
  'opacity', 'visibility', 'display',
  'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
  'font-family', 'font-size', 'font-weight', 'font-style', 'text-anchor',
  'offset', 'stop-color', 'stop-opacity',
  'gradientunits', 'gradienttransform', 'spreadmethod',
  'clip-path', 'clip-rule', 'mask', 'href', 'xlink:href',
  'paint-order', 'vector-effect'
];

/**
 * Inert layout containers.
 *
 * The custom HTML/CSS slide block lets an author lay a slide out by hand, and
 * hand-written layout is made of these. They are pure boxes — no scripting
 * surface, no URL sink — so allowing them costs nothing the strict profile was
 * protecting. They are kept out of the strict profile only because rich text
 * has no use for them.
 */
const LAYOUT_CONTAINER_TAGS = [
  'section', 'header', 'footer', 'nav', 'article', 'aside', 'main',
  'details', 'summary', 'dl', 'dt', 'dd'
];

/**
 * Removed outright. `style` is absent here because the embedded-document
 * profile needs it; the strict profile omits it from ALLOWED_TAGS instead.
 */
const FORBIDDEN_TAGS = [
  'script', 'iframe', 'object', 'embed', 'applet', 'frame', 'frameset',
  'form', 'input', 'button', 'textarea', 'select', 'option', 'optgroup',
  'base', 'meta', 'link', 'noscript',
  // Script vectors that live inside SVG/MathML rather than HTML
  'foreignobject', 'animate', 'animatetransform', 'animatemotion', 'set',
  'handler', 'listener'
];

const FORBIDDEN_ATTR = [
  // on* is stripped by DOMPurify regardless; these are the non-on* sinks.
  'srcdoc', 'formaction', 'action', 'ping', 'xlink:show', 'xlink:actuate',
  'http-equiv', 'dynsrc', 'lowsrc'
];

/**
 * Schemes permitted in href/src and friends.
 *
 * javascript:, vbscript: and data: all fail this. Relative URLs still pass —
 * the trailing alternatives are DOMPurify's own construction for that. data:
 * is re-permitted for images only, and only by the hook below.
 */
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

/**
 * Base64 raster images, which the whole app depends on: uploaded pictures,
 * pasted images, the school logo and fabric's draw-over backdrop are all
 * embedded as data: URLs rather than uploaded anywhere.
 *
 * image/svg+xml is deliberately absent — an SVG data URL is a script vector,
 * not a picture.
 */
const DATA_IMAGE_URI = /^data:image\/(?:png|jpe?g|gif|webp|avif|bmp|x-icon|vnd\.microsoft\.icon);base64,[a-z0-9+/]+=*$/i;

/** The only places a data: URL may appear. */
const DATA_IMAGE_SINKS: Record<string, string[]> = {
  // <img src="data:image/png;base64,...">
  img: ['src'],
  // fabric's toSVG() embeds the backdrop photo on an SVG <image> element, so
  // allowing it on <img> alone would break every draw-over that has one.
  image: ['href', 'xlink:href']
};

const isPermittedDataImage = (tagName: string, attrName: string, value: string): boolean => {
  const sinks = DATA_IMAGE_SINKS[tagName.toLowerCase()];
  if (!sinks || !sinks.includes(attrName.toLowerCase())) return false;
  return DATA_IMAGE_URI.test(value.replace(/\s+/g, ''));
};

/**
 * ALLOWED_URI_REGEXP rejects every data: URL, which is the safe default. This
 * hook hands back the narrow exception: a base64 raster image, on one of the
 * three attributes above, and nowhere else.
 *
 * Registered once, globally, and shared by both profiles — embedded documents
 * carry base64 images too. It fails closed: if the hook does not run, data:
 * images are dropped rather than let through.
 */
let hookRegistered = false;
const registerDataImageHook = (): void => {
  if (hookRegistered) return;
  hookRegistered = true;

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const value = data.attrValue || '';
    if (!/^\s*data:/i.test(value)) return;

    const tagName = (node as Element).tagName || '';
    if (isPermittedDataImage(tagName, data.attrName, value)) {
      // Vetted: keep it verbatim and skip the URI check that would reject it.
      data.forceKeepAttr = true;
      return;
    }

    // Any other data: URL — data:text/html, data:image/svg+xml, a data: href —
    // is dropped.
    data.keepAttr = false;
  });
};

const STRICT_CONFIG: Config = {
  ALLOWED_TAGS: [...RICH_TEXT_TAGS, ...SVG_SHAPE_TAGS],
  ALLOWED_ATTR: [...RICH_TEXT_ATTR, ...SVG_SHAPE_ATTR],
  FORBID_TAGS: FORBIDDEN_TAGS,
  FORBID_ATTR: FORBIDDEN_ATTR,
  ALLOWED_URI_REGEXP,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // Keep the text of a stripped element rather than deleting the words with it.
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false
};

/**
 * Whole uploaded documents and code-block previews. Wider than the strict
 * profile — page-structure tags and <style> survive, because that is what an
 * uploaded layout is made of — but the same script vectors are removed.
 *
 * Only ever used for content that renders inside a sandboxed iframe.
 */
const EMBEDDED_DOCUMENT_CONFIG: Config = {
  ALLOWED_TAGS: [
    ...RICH_TEXT_TAGS,
    ...SVG_SHAPE_TAGS,
    'style',
    'section', 'header', 'footer', 'nav', 'article', 'aside', 'main',
    'details', 'summary', 'dl', 'dt', 'dd',
    'audio', 'video', 'source', 'track', 'picture', 'canvas',
    'abbr', 'address', 'bdi', 'bdo', 'cite', 'data', 'dfn', 'kbd',
    'q', 'ruby', 'rt', 'rp', 'samp', 'time', 'var', 'wbr', 'label', 'fieldset',
    'legend', 'output', 'progress', 'meter', 'template'
  ],
  ALLOWED_ATTR: [
    ...RICH_TEXT_ATTR,
    ...SVG_SHAPE_ATTR,
    'controls', 'autoplay', 'loop', 'muted', 'poster', 'preload',
    'type', 'media', 'sizes', 'srcset', 'open', 'value', 'max', 'min',
    'datetime', 'cite', 'start', 'reversed', 'for', 'name', 'align', 'valign',
    'bgcolor', 'border', 'cellpadding', 'cellspacing'
  ],
  FORBID_TAGS: FORBIDDEN_TAGS,
  FORBID_ATTR: FORBIDDEN_ATTR,
  ALLOWED_URI_REGEXP,
  ALLOW_DATA_ATTR: true,
  ALLOW_ARIA_ATTR: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // Deliberately a fragment sanitizer, not WHOLE_DOCUMENT: all three callers
  // (EmbeddedHtmlRenderer, CodeBlock Preview, exportUtils) feed the result into
  // buildSrcDoc or an export template that supplies its own document shell.
  // Emitting <html>/<body> wrappers here would nest a document inside a
  // document.
  //
  // FORCE_BODY keeps <style> alive. Uploaded layouts are whole documents with
  // their CSS in <head>, and a plain fragment parse discards head content
  // outright — which would strip the styling from every embedded layout and
  // code preview. scopeHTML downstream depends on those <style> blocks
  // existing.
  FORCE_BODY: true,
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false
};

const toHtmlString = (dirty: unknown): string => {
  if (typeof dirty === 'string') return dirty;
  if (dirty === null || dirty === undefined) return '';
  return String(dirty);
};

/**
 * Sanitize a rich-text or SVG fragment for rendering into the app's own DOM.
 * This is the function every render site goes through.
 */
export const sanitizeHtml = (dirty: unknown): string => {
  const html = toHtmlString(dirty);
  if (!html) return '';
  registerDataImageHook();
  return DOMPurify.sanitize(html, STRICT_CONFIG) as unknown as string;
};

/**
 * Custom slide layouts: the strict profile plus inert layout containers.
 *
 * The custom HTML/CSS block renders in the app's own DOM — that is the whole
 * point of it, since its CSS has to be able to reach the slide (or the deck)
 * rather than being sealed inside an iframe. So it gets the strict allowlist,
 * widened only by boxes: no <style> (the block's CSS travels in its own field,
 * where customCss.js can scope it), no media, no form controls.
 *
 * data-* attributes are permitted here and nowhere else in the app's DOM,
 * because hand-written CSS routinely selects on them.
 */
export const SLIDE_LAYOUT_TAGS = [
  ...RICH_TEXT_TAGS,
  ...SVG_SHAPE_TAGS,
  ...LAYOUT_CONTAINER_TAGS
];

export const SLIDE_LAYOUT_ATTR = [...RICH_TEXT_ATTR, ...SVG_SHAPE_ATTR, 'open'];

/**
 * Tags removed from slide layouts. `style` is spelled out — it is already
 * absent from ALLOWED_TAGS, so naming it here changes nothing at runtime, but
 * it makes this list the honest answer to "what gets stripped?" for the brief
 * the block's editor hands to an LLM.
 */
export const SLIDE_LAYOUT_FORBIDDEN_TAGS = [...FORBIDDEN_TAGS, 'style'];

const SLIDE_LAYOUT_CONFIG: Config = {
  ALLOWED_TAGS: SLIDE_LAYOUT_TAGS,
  ALLOWED_ATTR: SLIDE_LAYOUT_ATTR,
  FORBID_TAGS: SLIDE_LAYOUT_FORBIDDEN_TAGS,
  FORBID_ATTR: FORBIDDEN_ATTR,
  ALLOWED_URI_REGEXP,
  ALLOW_DATA_ATTR: true,
  ALLOW_ARIA_ATTR: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false
};

/**
 * Sanitize a whole HTML document for rendering inside a sandboxed iframe.
 * Do not use this for anything that lands in the app's own DOM.
 */
export const sanitizeEmbeddedDocument = (dirty: unknown): string => {
  const html = toHtmlString(dirty);
  if (!html) return '';
  registerDataImageHook();
  return DOMPurify.sanitize(html, EMBEDDED_DOCUMENT_CONFIG) as unknown as string;
};

/**
 * Sanitize hand-written slide layout markup for rendering into the app's DOM.
 * Pull any <style> out first (see `extractStyleBlocks` in customCss.js): this
 * profile deletes style elements and their rules outright, so markup pasted
 * with its CSS attached would silently lose the styling otherwise.
 */
export const sanitizeSlideLayoutHtml = (dirty: unknown): string => {
  const html = toHtmlString(dirty);
  if (!html) return '';
  registerDataImageHook();
  return DOMPurify.sanitize(html, SLIDE_LAYOUT_CONFIG) as unknown as string;
};

/**
 * Field names that hold HTML at every site that renders them. Deliberately
 * short: a field that is rendered as *text* must not be sanitized, because
 * escaping would turn "Rock & Roll" into "Rock &amp; Roll" on screen. Anything
 * omitted here is still sanitized at render time, which is the real boundary.
 */
const HTML_BEARING_FIELDS = new Set(['content', 'caption', 'citation']);

const MAX_WALK_DEPTH = 64;

/**
 * Defence in depth for the import boundary: walk a loaded deck and clean the
 * HTML-bearing fields in place, after the structural pipeline has validated
 * the shape.
 *
 * Render-time sanitization stays the source of truth — this exists so a
 * malicious deck is defanged on the way in rather than relying on every future
 * render site remembering to be safe.
 */
export const sanitizeDeckHtml = <T,>(value: T, depth = 0): T => {
  if (depth > MAX_WALK_DEPTH || value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeDeckHtml(entry, depth + 1)) as unknown as T;
  }

  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    Object.keys(source).forEach((key) => {
      const entry = source[key];
      if (typeof entry === 'string' && HTML_BEARING_FIELDS.has(key)) {
        result[key] = sanitizeHtml(entry);
      } else {
        result[key] = sanitizeDeckHtml(entry, depth + 1);
      }
    });
    return result as unknown as T;
  }

  return value;
};

/** Exported for the tests, so the allowlist itself can be asserted on. */
export const __testing = {
  DATA_IMAGE_URI,
  ALLOWED_URI_REGEXP,
  RICH_TEXT_TAGS,
  SVG_SHAPE_TAGS,
  FORBIDDEN_TAGS
};

export default sanitizeHtml;
